import type { FullTranslation } from "../../lib/i18n"

// Shared by the Process block and the HowTo JSON-LD on the landing page, so
// the structured data always says what the page actually shows.

export const PROCESS_T = {
  eyebrow: { FR: "Comment ça marche", EN: "How it works" },
  headline: {
    FR: "De l'idée à la boîte mail, en 5 étapes.",
    EN: "From idea to inbox, in 5 steps.",
  },
  subhead: {
    FR: "Choisissez un métier, une ville, les enrichissements. Lancez le mailing. Recevez vos leads.",
    EN: "Pick an industry, a city, your enrichments. Launch the mailing. Get your leads.",
  },
  step_label: { FR: "Étape", EN: "Step" },

  step_1_title: { FR: "Choisissez un métier", EN: "Pick an industry" },
  step_1_body: {
    FR: "Dentiste, agence web, salle de sport, hôtel : n'importe quel vertical fonctionne.",
    EN: "Dentist, web agency, gym, hotel: any vertical works.",
  },

  step_2_title: { FR: "Choisissez où", EN: "Pick where" },
  step_2_body: {
    FR: "Une ville ou un pays entier. N'importe où sur Terre. Recherche live, pas de base figée.",
    EN: "A city or an entire country. Anywhere on Earth. Live search, no stale database.",
  },

  step_3_title: {
    FR: "Choisissez vos enrichissements",
    EN: "Pick your enrichments",
  },
  step_3_body: {
    FR: "Ajoutez seulement ce dont vous avez besoin : emails, téléphones, audit SEO, ratings, signaux d'ads.",
    EN: "Add only what you need: emails, phone numbers, SEO audit, ratings, ad signals.",
  },

  step_4_title: { FR: "Lancez le mailing", EN: "Launch the mailing" },
  step_4_body: {
    FR: "Une séquence cold email 3 étapes générée par lead, poussée dans votre Lemlist en un clic.",
    EN: "A 3-step cold email sequence generated per lead, pushed to your Lemlist in one click.",
  },

  step_5_title: { FR: "Recevez vos leads", EN: "Get your leads" },
  step_5_body: {
    FR: "Export CSV, upsert HubSpot, ou en direct dans le dashboard. Relancez quand vous voulez.",
    EN: "CSV export, HubSpot upsert, or live in the dashboard. Run it again whenever you want.",
  },

  v_step1_chip_dentist: { FR: "Dentiste", EN: "Dentist" },
  v_step1_chip_agency: { FR: "Agence web", EN: "Web agency" },
  v_step1_chip_hotel: { FR: "Hôtel", EN: "Hotel" },
  v_step1_chip_gym: { FR: "Salle de sport", EN: "Gym" },
  v_step1_chip_restaurant: { FR: "Restaurant", EN: "Restaurant" },
  v_step1_chip_law: { FR: "Cabinet d'avocats", EN: "Law firm" },

  v_step2_city_lyon: { FR: "Lyon, France", EN: "Lyon, France" },
  v_step2_city_berlin: { FR: "Berlin, Allemagne", EN: "Berlin, Germany" },
  v_step2_city_lisbon: { FR: "Lisbonne, Portugal", EN: "Lisbon, Portugal" },
  v_step2_city_tokyo: { FR: "Tokyo, Japon", EN: "Tokyo, Japan" },

  v_step3_email: { FR: "Email enrichment", EN: "Email enrichment" },
  v_step3_phone: { FR: "Phone enrichment", EN: "Phone enrichment" },
  v_step3_seo: { FR: "Website Quality Score", EN: "Website Quality Score" },
  v_step3_ads: { FR: "Ad signals", EN: "Ad signals" },
  v_step3_ratings: {
    FR: "Ratings multi-sources",
    EN: "Multi-source ratings",
  },
  v_step3_included: { FR: "Inclus", EN: "Included" },

  v_step4_seq: { FR: "Séquence 3 étapes", EN: "3-step sequence" },
  v_step4_d0: { FR: "J+0 · Intro", EN: "D+0 · Intro" },
  v_step4_d3: { FR: "J+3 · Relance", EN: "D+3 · Follow-up" },
  v_step4_d7: { FR: "J+7 · Dernière chance", EN: "D+7 · Last chance" },
  v_step4_pushed: { FR: "Poussé dans Lemlist", EN: "Pushed to Lemlist" },

  v_step5_lead1_name: { FR: "Clinique Lyon Dent", EN: "Clinique Lyon Dent" },
  v_step5_lead2_name: {
    FR: "Berliner Zahnzentrum",
    EN: "Berliner Zahnzentrum",
  },
  v_step5_lead3_name: { FR: "Lisbon Smile", EN: "Lisbon Smile" },
  v_step5_col_lead: { FR: "Lead", EN: "Lead" },
  v_step5_col_score: { FR: "SEO", EN: "SEO" },
  v_step5_col_status: { FR: "Statut", EN: "Status" },
  v_step5_status_sent: { FR: "Envoyé", EN: "Sent" },
  v_step5_status_open: { FR: "Ouvert", EN: "Opened" },
  v_step5_status_queued: { FR: "En file", EN: "Queued" },
} satisfies FullTranslation

/** Step numbers of the documented process, in order. */
export const PROCESS_STEP_NUMBERS = [1, 2, 3, 4, 5] as const

/** Translation keys of a process step. */
export function processStepKeys(num: (typeof PROCESS_STEP_NUMBERS)[number]) {
  return {
    titleKey: `step_${num}_title` as keyof typeof PROCESS_T,
    bodyKey: `step_${num}_body` as keyof typeof PROCESS_T,
  }
}
